import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import {
  CreateBusinessContainer,
  LeftColumn,
  RightColumn,
  BenefitsList,
  FormWrapper,
  FormRow,
} from './CreateBusiness.styled';
import { TextField, Button, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import logo from '../../../src/assets/images/newLogoSnippet.jpg';
import { businessService } from '../../services/api/businessService';
import { accountService } from '../../services/api/accountService';
import { SERVICE_AREA_STATE_CODES } from '../../utils/serviceAreaStates';
import { formatPhoneInputMask, phoneDigitsForApi } from '../../utils/phoneInputMask';

const CreateBusiness = () => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    BusinessName: '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    State: '',
    ZipCode: '',
    BusinessEmail: '',
    BusinessPhoneNumber: '',
    BusinessWebsiteUrl: '',
    BusinessAbout: '',
    BusinessPhoto: null,
    agreedToBusinessTerms: false,
  });

  const [businessPhoto, setBusinessPhoto] = useState(null);

  const [errors, setErrors] = useState({
    BusinessName: '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    State: '',
    ZipCode: '',
    BusinessEmail: '',
    BusinessPhoneNumber: '',
    BusinessWebsiteUrl: '',
    BusinessAbout: '',
    BusinessPhoto: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  //Check if we arrived here properly from signup
  if (!location.state?.navigateFromSignUp) {
    return <Navigate to="/" replace />;
  }

  const validateBusinessName = (name) => {
    if (name.length < 1) {
      return 'Business name is required';
    }
    return '';
  };

  const validateAddressLine1 = (address) => {
    if (address.length < 1) {
      return 'Address is required';
    }
    return '';
  };

  const validateCity = (city) => {
    if (city.length < 1) {
      return 'City is required';
    }
    return '';
  };

  const validateState = (state) => {
    if (state.length < 1) {
      return 'State is required';
    }
    if (state.length > 2) {
      return 'State must be 2 characters';
    }
    if (!SERVICE_AREA_STATE_CODES.includes(state)) {
      return 'Please enter KY, IN, or OH';
    }
    return '';
  };

  const validateZipCode = (zipCode) => {
    if (zipCode.length < 1) {
      return 'ZipCode is required';
    }
    if (zipCode.length > 0 && zipCode.length !== 5) {
      return 'ZipCode must be 5 digits';
    }
    return '';
  };

  const validateBusinessEmail = (email) => {
    if (email.length < 1) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateBusinessPhoneNumber = (phoneNumber) => {
    if (phoneNumber.length < 1) {
      return 'Phone number is required';
    }
    if (!/^\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
      return 'Please enter a 10-digit phone number';
    }
    return '';
  };

  const validateBusinessWebsiteUrl = (url) => {
    if (!url) {
      return '';
    }
    if (url.length < 1) {
      return 'Website URL is required';
    }
    const urlRegex =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    if (!urlRegex.test(url)) {
      return 'Please enter a valid URL';
    }
    return '';
  };

  const validateBusinessAbout = (about) => {
    if (about.length < 1) {
      return 'About is required';
    }
    return '';
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';

    switch (name) {
      case 'BusinessName':
        error = validateBusinessName(value);
        break;
      case 'AddressLine1':
        error = validateAddressLine1(value);
        break;
      case 'City':
        error = validateCity(value);
        break;
      case 'State':
        error = validateState(value);
        break;
      case 'ZipCode':
        error = validateZipCode(value);
        break;
      case 'BusinessEmail':
        error = validateBusinessEmail(value);
        break;
      case 'BusinessPhoneNumber':
        error = validateBusinessPhoneNumber(value);
        break;
      case 'BusinessWebsiteUrl':
        error = validateBusinessWebsiteUrl(value);
        break;
      case 'BusinessAbout':
        error = validateBusinessAbout(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'City') {
      const onlyLetters = value.replace(/[^a-zA-Z]/g, '');
      setFormData({ ...formData, [name]: onlyLetters });
    } else if (name === 'State') {
      const upperValue = value.toUpperCase().slice(0, 2); // Limit to 2 characters
      setFormData((prev) => ({
        ...prev,
        [name]: upperValue,
      }));
    }
    // Handle ZipCode validation
    else if (name === 'ZipCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 5);
      setFormData({ ...formData, [name]: numericValue });
    }
    else if (name === 'BusinessPhoneNumber') {
      setFormData((prev) => ({
        ...prev,
        [name]: formatPhoneInputMask(value),
      }));
      if (errors.BusinessPhoneNumber !== '') {
        setErrors((prev) => ({ ...prev, BusinessPhoneNumber: '' }));
      }
      return;
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (name === 'BusinessName' && errors.BusinessName !== '') {
      setErrors((prev) => ({ ...prev, BusinessName: '' }));
    } else if (name === 'AddressLine1' && errors.AddressLine1 !== '') {
      setErrors((prev) => ({ ...prev, AddressLine1: '' }));
    } else if (name === 'City' && errors.City !== '') {
      setErrors((prev) => ({ ...prev, City: '' }));
    } else if (name === 'State' && errors.State !== '') {
      setErrors((prev) => ({ ...prev, State: '' }));
    } else if (name === 'ZipCode' && errors.ZipCode !== '') {
      setErrors((prev) => ({ ...prev, ZipCode: '' }));
    } else if (name === 'BusinessEmail' && errors.BusinessEmail !== '') {
      setErrors((prev) => ({ ...prev, BusinessEmail: '' }));
    } else if (
      name === 'BusinessPhoneNumber' &&
      errors.BusinessPhoneNumber !== ''
    ) {
      setErrors((prev) => ({ ...prev, BusinessPhoneNumber: '' }));
    } else if (
      name === 'BusinessWebsiteUrl' &&
      errors.BusinessWebsiteUrl !== ''
    ) {
      setErrors((prev) => ({ ...prev, BusinessWebsiteUrl: '' }));
    } else if (name === 'BusinessAbout' && errors.BusinessAbout !== '') {
      setErrors((prev) => ({ ...prev, BusinessAbout: '' }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setErrors((prev) => ({
          ...prev,
          BusinessPhoto: 'File size must be less than 5MB',
        }));
        setBusinessPhoto(null);
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({
          ...prev,
          BusinessPhoto: 'File must be an image',
        }));
        setBusinessPhoto(null);
        return;
      }
      setErrors((prev) => ({ ...prev, BusinessPhoto: '' }));
      setBusinessPhoto(file);
    }
  };

  const isFormValid = () => {
    const hasErrors = Object.values(errors).some((error) => error !== '');

    const requiredFields = {
      BusinessName: formData.BusinessName,
      AddressLine1: formData.AddressLine1,
      City: formData.City,
      State: formData.State,
      ZipCode: formData.ZipCode,
      BusinessEmail: formData.BusinessEmail,
      BusinessPhoneNumber: formData.BusinessPhoneNumber,
      BusinessAbout: formData.BusinessAbout,
    };

    const hasEmptyFields = Object.values(requiredFields).some(
      (value) => !value,
    );

    return !hasErrors && !hasEmptyFields && formData.agreedToBusinessTerms;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors((prev) => ({ ...prev, BusinessPhoto: '' })); // Clear any previous photo errors

    try {
      let photoUrl = null;
      if (businessPhoto) {
        try {
          photoUrl = await businessService.uploadBusinessImage(
            businessPhoto,
            businessPhoto.name,
          );
        } catch (error) {
          setIsLoading(false);
          throw new Error('Failed to upload photo');
        }
      }

      const response = await businessService.createBusiness({
        BusinessName: formData.BusinessName,
        BusinessAddress: {
          AddressLine1: formData.AddressLine1,
          AddressLine2: formData.AddressLine2,
          City: formData.City,
          State: formData.State,
          ZipCode: formData.ZipCode,
        },
        BusinessEmail: formData.BusinessEmail,
        BusinessPhoneNumber: phoneDigitsForApi(formData.BusinessPhoneNumber),
        BusinessWebsiteUrl: formData.BusinessWebsiteUrl,
        BusinessAbout: formData.BusinessAbout,
        BusinessPublished: false,
        BusinessPhotoUrl: photoUrl,
      });
      await accountService.updateAccount({
        BusinessId: response.BusinessId,
      });
      navigate('/');
    } catch (error) {
      console.error('Error creating business:', error);
      alert('Error creating business');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CreateBusinessContainer>
      <LeftColumn>
        <img
          src={logo}
          alt="NKY Connect Logo"
          style={{ width: '150px', marginBottom: '1.5rem' }}
        />
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mt: 4, textAlign: 'center' }}
        >
          Why List Your Business?
        </Typography>
        <BenefitsList>
          <li>
            <CheckCircleOutlineIcon />
            Reach thousands of potential customers in Northern Kentucky
          </li>
          <li>
            <CheckCircleOutlineIcon />
            Create your own business landing page to share via social media
          </li>
          <li>
            <CheckCircleOutlineIcon />
            Receive booking requests directly through our platform
          </li>
          <li>
            <CheckCircleOutlineIcon />
            Access detailed analytics and customer insights
          </li>
          <li>
            <CheckCircleOutlineIcon />
            Connect with other local businesses
          </li>
          <li>
            <CheckCircleOutlineIcon />
            Get featured in our promotional campaigns
          </li>
        </BenefitsList>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Join our growing community of successful local businesses and start
            expanding your reach today!
          </Typography>
        </Box>
      </LeftColumn>

      <RightColumn>
        <FormWrapper>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            textAlign="center"
          >
            Create Your Business Profile
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Business Name"
              name="BusinessName"
              value={formData.BusinessName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.BusinessName}
              helperText={errors.BusinessName}
              required
              fullWidth
            />
            <TextField
              label="Address Line 1"
              name="AddressLine1"
              value={formData.AddressLine1}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.AddressLine1}
              helperText={errors.AddressLine1}
              required
              fullWidth
            />
            <TextField
              label="Address Line 2"
              name="AddressLine2"
              value={formData.AddressLine2}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.AddressLine2}
              helperText={errors.AddressLine2}
              fullWidth
            />
            <FormRow>
              <TextField
                label="City"
                name="City"
                value={formData.City}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.City}
                helperText={errors.City}
                required
                fullWidth
              />
              <TextField
                label="State"
                name="State"
                value={formData.State}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.State}
                helperText={errors.State}
                required
                fullWidth
                inputProps={{
                  maxLength: 2,
                  style: { textTransform: 'uppercase' },
                }}
              />
            </FormRow>

            <TextField
              label="Zipcode"
              name="ZipCode"
              value={formData.ZipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.ZipCode}
              helperText={errors.ZipCode}
              required
              fullWidth
              inputProps={{ maxLength: 5 }}
            />
            <TextField
              label="Business Email"
              name="BusinessEmail"
              value={formData.BusinessEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.BusinessEmail}
              helperText={errors.BusinessEmail}
              required
              fullWidth
            />
            <TextField
              label="Business Phone Number"
              name="BusinessPhoneNumber"
              type="tel"
              value={formData.BusinessPhoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="555-123-4567"
              inputProps={{ inputMode: 'numeric', autoComplete: 'tel' }}
              error={!!errors.BusinessPhoneNumber}
              helperText={errors.BusinessPhoneNumber}
              required
              fullWidth
            />
            <TextField
              label="Business Website URL"
              name="BusinessWebsiteUrl"
              value={formData.BusinessWebsiteUrl}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.BusinessWebsiteUrl}
              helperText={errors.BusinessWebsiteUrl}
              fullWidth
            />
            <TextField
              label="About Your Business"
              name="BusinessAbout"
              value={formData.BusinessAbout}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.BusinessAbout}
              helperText={errors.BusinessAbout}
              required
              fullWidth
              multiline
              rows={4}
            />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                fullWidth
                error={!!errors.BusinessPhoto}
              >
                {businessPhoto
                  ? 'Change Business Photo'
                  : 'Upload Business Photo'}
                <input
                  accept="image/*"
                  type="file"
                  hidden
                  onChange={handlePhotoChange}
                />
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  textAlign: 'center',
                }}
              >
                Recommended: 16:9 or 4:3 aspect ratio
              </Typography>
              {businessPhoto && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {businessPhoto.name}
                </Typography>
              )}
              {errors.BusinessPhoto && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.BusinessPhoto}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreedToBusinessTerms}
                    onChange={handleChange}
                    name="agreedToBusinessTerms"
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link to="/business-terms" target="_blank" rel="noopener noreferrer">
                      Business Terms of Service
                    </Link>
                  </Typography>
                }
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={!isFormValid() || isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Business Profile'}
            </Button>
          </form>
        </FormWrapper>
      </RightColumn>
    </CreateBusinessContainer>
  );
};

export default CreateBusiness;
